import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getSharedDiagnosis } from "../lib/api";
import { useAuth } from "../lib/auth";
import { track } from "@/lib/track";
import { Helmet } from "react-helmet-async";
import GitHubStarsLink from "../components/GitHubStarsLink";

function verdictStyle(v) {
  if (v === "pursue")
    return { badge: "badge-pursue", text: "verdict-pursue", label: "PURSUE" };
  if (v === "pivot")
    return { badge: "badge-pivot", text: "verdict-pivot", label: "PIVOT" };
  if (v === "kill")
    return { badge: "badge-kill", text: "verdict-kill", label: "KILL" };
  return { badge: "badge-neutral", text: "", label: "—" };
}

function ScoreRow({ label, val, invert = false }) {
  const v = val ?? 0;
  const color = invert
    ? v >= 70
      ? "#ef4444"
      : v >= 40
        ? "#f59e0b"
        : "#059669"
    : v >= 70
      ? "#059669"
      : v >= 40
        ? "#f59e0b"
        : "#ef4444";
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-display text-base font-medium tracking-tight">
          {label}
        </span>
        <span className="font-mono text-xs text-zinc-500">{v}/100</span>
      </div>
      <div className="h-[3px] bg-zinc-100 rounded-full overflow-hidden">
        <div style={{ width: `${v}%`, background: color }} className="h-full" />
      </div>
    </div>
  );
}

export default function SharedDiagnosis() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await getSharedDiagnosis(slug);
        setData(d);

        if (d?.diagnosis) {
          track("result_viewed", { type: "shared" });
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const verdictTarget = user ? "/dashboard" : "/";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="overline">loading…</div>
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="overline mb-3">not found</div>
          <h1 className="font-display text-4xl font-semibold">
            This verdict is private or revoked.
          </h1>
          <Link to={verdictTarget} className="btn btn-primary mt-6">
            Get your own verdict →
          </Link>
        </div>
      </div>
    );
  }

  const { idea, founder, diagnosis } = data;
  const s = verdictStyle(diagnosis.verdict);

  return (
    <>
      <Helmet>
        <title>
          {`${idea?.title || "Startup Idea"} - Should You Build This Startup Idea? (AI Verdict)`}
        </title>

        <meta
          name="description"
          content={`Should you build "${idea?.one_liner}"? Get an AI-powered verdict: pursue, pivot, or kill it, with clear risks and next steps.`}
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={`${idea?.title || "Startup Idea"} - Should You Build This Startup Idea? (AI Verdict)`}
        />
        <meta
          property="og:description"
          content={`Should you build "${idea?.one_liner}"? Get an AI-powered verdict: pursue, pivot, or kill it, with clear risks and next steps.`}
        />
        <meta
          property="og:image"
          content="https://killswitch.softwired.in/og-image.png"
        />

        {/* Twitter */}
        <meta
          name="twitter:title"
          content={`${idea?.title || "Startup Idea"} - Should You Build This Startup Idea? (AI Verdict)`}
        />
        <meta
          name="twitter:description"
          content={`Should you build "${idea?.one_liner}"? Get an AI-powered verdict: pursue, pivot, or kill it, with clear risks and next steps.`}
        />
        <meta
          name="twitter:image"
          content="https://killswitch.softwired.in/og-image.png"
        />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href={`https://killswitch.softwired.in/startup-idea/${slug}`}
        />

        {/* Robots */}
        <meta
          name="robots"
          content={data.is_indexable ? "index, follow" : "noindex, nofollow"}
        />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Top bar */}
        <nav className="border-b border-zinc-200">
          <div className="max-w-5xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2"
              data-testid="share-brand"
            >
              <div className="w-6 h-6 bg-zinc-950 rounded-sm flex items-center justify-center">
                <span className="text-white font-mono text-[10px] font-bold">
                  KS
                </span>
              </div>
              <div className="leading-none">
                <div className="font-display font-semibold text-lg tracking-tight">
                  Killswitch
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">
                  by Softwired
                </div>
              </div>
            </Link>
            <div className="flex items-center gap-2">
              <GitHubStarsLink
                className="hidden lg:flex items-center gap-2 text-[15px] font-medium tracking-tight text-zinc-600 hover:text-zinc-950 transition-colors"
              />
              <Link
                to="/contact"
                className="btn btn-ghost text-sm"
                data-testid="share-contact-link"
              >
                Contact
              </Link>
              <Link
                data-testid="share-get-verdict"
                className="btn btn-primary text-sm"
                to={verdictTarget}
              >
                Get your verdict →
              </Link>
            </div>
          </div>
        </nav>

        <main className="max-w-3xl mx-auto px-6 md:px-8 py-16">
          {/* Attribution */}
          <div className="flex items-center gap-3 mb-8 fade-up">
            {founder.picture ? (
              <img
                alt={founder.name}
                src={founder.picture}
                className="w-8 h-8 rounded-full border border-zinc-200"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-zinc-100 border border-zinc-200" />
            )}
            <div className="text-sm">
              <span className="text-zinc-600">
                {founder.name} shared a verdict from{" "}
              </span>
              <span className="font-medium">Killswitch</span>
            </div>
          </div>

          {/* Verdict headline */}
          <div className="fade-up">
            <div className="flex items-center gap-3 mb-5">
              <span className={`badge ${s.badge}`} data-testid="shared-verdict">
                {s.label}
              </span>
              <span className="font-mono text-xs text-zinc-500 uppercase tracking-[0.15em]">
                {new Date(
                  diagnosis.created_at_ist || diagnosis.created_at,
                ).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}
              </span>
            </div>
            <h1 className="sr-only">
              {`Is "${idea?.title}" - ${idea?.one_liner}, a Good Startup Idea? AI Analysis and Verdict`}
            </h1>
            <h1
              className={`font-display text-4xl sm:text-6xl font-semibold tracking-tighter leading-[1.02] mb-6 ${s.text}`}
              data-testid="shared-headline"
            >
              {diagnosis.verdict_headline}
            </h1>
            <div className="sr-only">
              <p>
                This page evaluates whether "{idea?.title}" is a good startup
                idea. It answers questions like: is this startup idea worth
                building, how to validate a startup idea, and what are the risks
                and opportunities of this business idea.
              </p>

              <p>
                The analysis includes startup idea validation, founder-market
                fit, willingness to pay, execution risk, and actionable steps to
                test this idea in real market conditions.
              </p>
            </div>
            <div className="overline mb-2">the idea</div>
            <h2 className="font-display text-2xl font-medium tracking-tight mb-1">
              {idea.title}
            </h2>
            <p className="text-zinc-600 text-lg">{idea.one_liner}</p>
          </div>

          <div className="hairline my-12" />

          <div className="grid sm:grid-cols-2 gap-8 fade-up">
            <div>
              <div className="overline mb-3">diagnosis</div>
              <p className="text-base text-zinc-800 leading-relaxed">
                {diagnosis.diagnosis}
              </p>
            </div>
            <div>
              <div className="overline mb-3">what they&apos;re missing</div>
              <p className="font-serif italic text-xl leading-snug text-zinc-950">
                {diagnosis.key_insight}
              </p>
            </div>
          </div>

          <div className="card p-6 mt-10 fade-up">
            <div className="overline mb-3">startup idea evaluation</div>

            <h2 className="font-display text-lg font-medium mb-2">
              Is "{idea?.title}" a good startup idea?
            </h2>

            <p className="text-sm text-zinc-800 leading-relaxed">
              This analysis by Killswitch, an AI startup idea validator,
              evaluates the idea based on founder-market fit, willingness to
              pay, execution risk, and real-world momentum.
            </p>

            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              The verdict (pursue, pivot, or kill) helps founders decide whether
              to build this startup and what actions to take next.
            </p>
          </div>

          {/* Scores */}
          <div className="card p-6 mt-6 space-y-5 fade-up">
            <div className="overline">the honest scores</div>
            <ScoreRow label="Founder-market fit" val={diagnosis.fmf_score} />
            <ScoreRow label="Willingness to pay" val={diagnosis.wtp_score} />
            <ScoreRow label="Momentum" val={diagnosis.momentum_score} />
            <ScoreRow
              label="Execution risk"
              val={diagnosis.execution_risk}
              invert
            />
          </div>

          {/* Competitor */}
          {diagnosis.competitor_signal && (
            <div className="card p-6 mt-6 fade-up">
              <div className="overline mb-3">competitor signal</div>
              <p className="text-sm text-zinc-800 leading-relaxed">
                {diagnosis.competitor_signal}
              </p>
            </div>
          )}

          {/* Actions teaser */}
          {diagnosis.this_week_actions?.length > 0 && (
            <div className="card p-6 mt-6 fade-up">
              <div className="overline mb-3">the 5 things to do this week</div>
              <ul className="space-y-3">
                {diagnosis.this_week_actions.map((a, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="font-mono text-xs text-zinc-400 pt-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="font-display text-base font-medium">
                        {a.title}
                      </div>
                      <div className="text-sm text-zinc-600">{a.why}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-5 text-xs text-zinc-500 italic font-serif">
                Exact scripts, target names, and assumptions are private to the
                founder.
              </div>
            </div>
          )}

          {/* Final CTA */}
          <div className="mt-16 border-t border-zinc-200 pt-12 text-center fade-up">
            <h3 className="font-display text-3xl sm:text-4xl font-semibold tracking-tighter mb-4">
              Want a verdict on{" "}
              <span className="font-serif italic text-zinc-500">your</span>{" "}
              idea?
            </h3>
            <p className="text-zinc-600 mb-8 max-w-xl mx-auto">
              Brutally honest in under 2 minutes. Pursue, pivot, or kill — with
              the exact 5 actions to take this week.
            </p>
            <Link
              data-testid="shared-cta"
              className="btn btn-primary text-base px-6 py-3"
              to={verdictTarget}
            >
              Diagnose my idea →
            </Link>
          </div>

          <footer className="mt-24 text-center text-xs text-zinc-500 font-mono uppercase tracking-[0.15em] space-y-2">
            <div>
              Killswitch | AI Startup Idea Validator (Pursue, Pivot, or Kill It)
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-center sm:gap-4">
              <span>Built by Softwired</span>
              <a
                href="https://softwired.in"
                target="_blank"
                rel="noreferrer"
                className="hover:text-zinc-950 transition-colors"
              >
                softwired.in
              </a>
              <a
                href="mailto:admin@softwired.in"
                className="hover:text-zinc-950 transition-colors"
              >
                admin@softwired.in
              </a>
            </div>
            <p className="text-xs text-zinc-500 mt-6">
              Explore more startup ideas and AI validation insights on
              Killswitch.
            </p>
          </footer>
        </main>
      </div>
    </>
  );
}
